import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "@apollo/client/react";
import { ArrowLeft, Loader2 } from "lucide-react";

import { CREATE_STUDENT } from "@/graphql/mutations/students";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { exerciseCountValue } from "@/lib/students";
import { StudentFormFields } from "@/components/students/student-form";
import { toast } from "sonner";

export default function NewStudentPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [createStudent] = useMutation(CREATE_STUDENT);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData(event.currentTarget);
    // Every part of a birth date is optional, so an empty field is left out
    // rather than sent as anything. The age is never typed — it is derived from
    // the birth year.
    const number = (field: string) => {
      const raw = (formData.get(field) as string)?.trim();
      return raw ? parseInt(raw, 10) : undefined;
    };
    const input = {
      name: formData.get("name") as string,
      birthYear: number("birthYear"),
      birthMonth: number("birthMonth"),
      birthDay: number("birthDay"),
      firstGradeYear: number("firstGradeYear"),
      notes: (formData.get("notes") as string) || undefined,
      defaultExerciseCount: exerciseCountValue(formData.get("defaultExerciseCount")),
      requireDiscussionResolution: formData.get("requireDiscussionResolution") === "on",
      email: (formData.get("email") as string) || undefined,
      password: (formData.get("password") as string) || undefined,
    };

    try {
      const { data } = await createStudent({ variables: input });
      const errors: string[] = data?.createStudent?.errors ?? [];
      if (errors.length > 0) {
        toast.error(errors.join("; "));
        return;
      }
      // Three different outcomes, and a grown-up who typed an address has to be
      // told which: the learner is not in the family and cannot sign in until
      // that address is confirmed, and if the mail did not leave there is
      // nothing at all in flight for them to wait for.
      const created = data.createStudent.student;
      if (!created.emailPending) {
        toast.success("Mokinys sukurtas sėkmingai");
      } else if (data.createStudent.emailed) {
        toast.success("Mokinys sukurtas. Patvirtinimo nuorodą išsiuntėme el. paštu", {
          description:
            "Kol mokinys jos nepaspaus, jis neprisijungs ir šeimoje dar nebus matomas.",
        });
      } else {
        toast.warning("Mokinys sukurtas, bet laiško išsiųsti nepavyko", {
          description:
            "Kol el. paštas nepatvirtintas, mokinys neprisijungs. Nuorodos galima paprašyti prisijungimo lange.",
        });
      }
      navigate(`/students/${created.id}`);
    } catch {
      toast.error("Nepavyko sukurti mokinio");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/students">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Naujas mokinys</h1>
          <p className="text-muted-foreground">
            Užpildykite mokinio informaciją
          </p>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        <Card className="max-w-3xl gap-0 py-0">
          <StudentFormFields disabled={isLoading} />

          <div className="flex flex-wrap gap-2 border-t px-6 py-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sukurti mokinį
            </Button>
            <Button type="button" variant="ghost" asChild>
              <Link to="/students">Atšaukti</Link>
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
